import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { Formik, Form } from 'formik';

import { notifySuccess } from '@/portainer/services/notifications';
import { useApplyDenyPortainerAccessMutation } from '@/react/portainer/users/teams/queries/useApplyDenyPortainerAccessMutation';
import {
  getTeamsToApplyDenyPortainerAccess,
  useTeamsDenyingPortainerAccess,
} from '@/react/portainer/users/teams/queries/useTeamsDenyingPortainerAccess';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { Stepper } from '@@/Stepper/Stepper';
import { StepConfig, useWizardSteps } from '@@/Stepper/useWizardSteps';
import { StickyFooter } from '@@/StickyFooter/StickyFooter';
import { Button, LoadingButton } from '@@/buttons';

import { useAddon } from '../queries/useAddon';
import { useInstallAddon } from '../queries/useInstallAddon';

import { ConfigurationStep } from './ConfigurationStep';
import { AssignTeamsStep } from './AssignTeamsStep';
import { InstallFormValues } from './types';
import { trimValues } from './trimValues';

// TODO: each add-on defines its own configuration fields (and some may not
// need a wizard at all), so this hard-coded list becomes a per-addon step map
// once the install forms are built.
const steps: Array<StepConfig> = [
  { id: 'configuration', label: 'Configuration' },
  { id: 'assign-teams', label: 'Assign Teams' },
];

const initialValues: InstallFormValues = {
  version: '',
  values: {},
  teams: [],
  denyPortainerAccess: false,
};

export function AddonInstallView() {
  const { params } = useCurrentStateAndParams();
  const router = useRouter();
  const addonId = String(params.id ?? '');
  const addonQuery = useAddon(addonId);
  const displayName = addonQuery.data?.displayName ?? 'add-on';

  const installMutation = useInstallAddon();
  const applyDenyPortainerAccess = useApplyDenyPortainerAccessMutation();
  const denyingTeamIds = useTeamsDenyingPortainerAccess();

  const {
    currentStepIndex,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToStepByIndex,
  } = useWizardSteps({ steps });

  return (
    <div className="pb-20">
      <PageHeader
        title={`Set up ${displayName}`}
        breadcrumbs={[
          { label: 'Add-on management', link: 'portainer.addons' },
          { label: 'Install' },
          displayName,
        ]}
        reload
      />
      <div className="row">
        <p className="col-sm-12 mb-0 text-sm text-[var(--text-muted-color)]">
          Discover tools that integrate seamlessly with your workflow and boost
          productivity.
        </p>
      </div>

      <Formik<InstallFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ validateForm, submitForm, isValid, values }) => {
          // Required config fields live on the first step; block Next until
          // they're valid so the user can't skip past them.
          const hasConfigErrors = !isValid && values.version === '';

          return (
            <Form noValidate>
              <div className="row mt-4">
                <div className="col-sm-12 flex flex-col gap-4">
                  <Stepper
                    steps={steps}
                    currentStepIndex={currentStepIndex}
                    onStepClick={goToStepByIndex}
                  />
                  <Widget aria-label={steps[currentStepIndex].label}>
                    <WidgetBody>
                      {/* All steps stay mounted (hidden when inactive) so a
                        user's selections survive navigating back and forth. */}
                      <div hidden={currentStepIndex !== 0}>
                        <ConfigurationStep />
                      </div>
                      <div hidden={currentStepIndex !== 1}>
                        {/* Always offered: the add-on being installed is itself a
                          landing target, so this can't wait for it to be enabled. */}
                        <AssignTeamsStep showDenyPortainerAccess />
                      </div>
                    </WidgetBody>
                  </Widget>
                </div>
              </div>

              <StickyFooter className="justify-end gap-4">
                {!isFirstStep && (
                  <Button
                    color="default"
                    size="medium"
                    type="button"
                    onClick={goToPreviousStep}
                    data-cy="addon-install-back-button"
                  >
                    Back
                  </Button>
                )}
                {isLastStep ? (
                  <LoadingButton
                    color="primary"
                    size="medium"
                    // Deliberately not a native submit button: the primary footer
                    // button is reconciled into the same DOM node as Next, and a
                    // type="submit" swap would let the click that navigated to
                    // this step also submit the form. Submit explicitly instead.
                    type="button"
                    isLoading={installMutation.isLoading}
                    disabled={hasConfigErrors}
                    loadingText="Validating"
                    onClick={async () => {
                      // Config-field errors live on the (hidden) first step, so
                      // jump back to it when Install is blocked by one.
                      const validationErrors = await validateForm();
                      if (
                        validationErrors.values &&
                        Object.keys(validationErrors.values).length > 0
                      ) {
                        goToStepByIndex(0);
                        return;
                      }
                      submitForm();
                    }}
                    data-cy="addon-install-submit-button"
                  >
                    Install
                  </LoadingButton>
                ) : (
                  <Button
                    color="primary"
                    size="medium"
                    type="button"
                    onClick={goToNextStep}
                    disabled={hasConfigErrors}
                    data-cy="addon-install-next-button"
                  >
                    Next
                  </Button>
                )}
              </StickyFooter>
            </Form>
          );
        }}
      </Formik>
    </div>
  );

  function handleSubmit(values: InstallFormValues) {
    installMutation.mutate(
      {
        id: addonId,
        version: values.version,
        values: trimValues(values.values),
        teams: values.teams,
      },
      {
        onSuccess: async () => {
          // Awaited so a failure surfaces before we navigate; the install has
          // already started, so we move on either way.
          const teamsToUpdate = getTeamsToApplyDenyPortainerAccess(
            values.teams,
            denyingTeamIds,
            values.denyPortainerAccess
          );

          if (teamsToUpdate.length > 0) {
            try {
              await applyDenyPortainerAccess.mutateAsync({
                teamIds: teamsToUpdate,
                denyPortainerAccess: values.denyPortainerAccess,
              });
            } catch {
              // Already surfaced via the mutation's error toast.
            }
          }
          notifySuccess('Add-on installation started', displayName);
          router.stateService.go('portainer.addons');
        },
      }
    );
  }
}
